import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { TABS } from '../tabs/tabs-data';

// Utility tabs that live outside the §6 content set but should still
// appear in the Explore dropdown so they\'re reachable from any tab.
const UTILITY_TABS = [
  { slug: 'what-is-dideck', title: 'What is DI Deck', glyph: '★' },
  { slug: 'faq', title: 'FAQ', glyph: '?' },
  { slug: 'pricing', title: 'Pricing', glyph: '€' },
] as const;

const EXPLORE_TABS = [...TABS.map((t) => ({ slug: t.slug, title: t.title, glyph: t.glyph })), ...UTILITY_TABS];

/**
 * Persistent top navigation shown above every tab detail page.
 * Per PRD §C.2: [D logo] [Explore ▾] on the left, [Pricing] CTA on the right.
 * "Explore" opens a full-width panel listing every tab so users navigate
 * between siblings without going back to home.
 */
export function TabHeader(): JSX.Element {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-40 border-b border-sovereign-line bg-brand-purple-dark text-white shadow-card">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 md:px-8">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            aria-label="DI Deck home"
            className="flex items-center gap-2 rounded-md px-1 py-0.5 hover:bg-white/10"
          >
            <span className="grid h-8 w-8 place-items-center rounded-md bg-white text-lg font-black text-brand-purple">
              D
            </span>
            <span className="text-sm font-bold tracking-wide">DI DECK</span>
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="tab-explore-panel"
            className="hidden items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold text-white/85 hover:bg-white/10 hover:text-white md:inline-flex"
          >
            Explore
            <span aria-hidden className={`transition-transform ${open ? 'rotate-180' : ''}`}>
              ▾
            </span>
          </button>
        </div>

        <Link
          to="/pricing"
          className="rounded-md bg-white px-3 py-1.5 text-xs font-bold text-brand-purple shadow-card hover:bg-white/95"
        >
          Pricing
        </Link>
      </div>

      {open && (
        <div
          id="tab-explore-panel"
          className="border-t border-white/10 bg-brand-purple-dark px-4 py-4 md:px-8"
        >
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {EXPLORE_TABS.map((t) => (
              <Link
                key={t.slug}
                to={`/${t.slug}`}
                className={
                  location.pathname === `/${t.slug}`
                    ? 'flex items-center gap-2 rounded-md bg-white/15 px-2.5 py-1.5 text-[12px] font-semibold text-white'
                    : 'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[12px] text-white/80 hover:bg-white/10 hover:text-white'
                }
              >
                <span aria-hidden className="text-sm">
                  {t.glyph}
                </span>
                <span className="truncate">{t.title}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
